import {
  Button,
  Typography,
  Stack,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material'
import type React from 'react'
import { useState } from 'react'

import { RequestType } from '@/types/Recommendation'

import { useCancelRequest } from './useCancelRequest'

interface Props {
  applicationId: string
  customerId: string
  subjectId: string
  recommendationId: string
  requestType?: RequestType
  onCancelled?: () => void
  userId?: string
}

type Stage = 'idle' | 'confirm' | 'success'

const CancelRequest: React.FC<Props> = ({
  applicationId,
  customerId,
  subjectId,
  recommendationId,
  requestType = RequestType.OPEN_BANKING,
  onCancelled,
  userId,
}) => {
  const [stage, setStage] = useState<Stage>('idle')
  const [notifyApplicant, setNotifyApplicant] = useState<boolean>(false)

  const { loading, error, cancelRequest, requestTypeText } = useCancelRequest({
    applicationId,
    customerId,
    subjectId,
    recommendationId,
    requestType,
    userId,
    onCancelled: () => {
      setStage('success')
      onCancelled?.()
    },
  })

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotifyApplicant(event.target.checked)
  }

  const handleCancel = () => {
    if (stage === 'success') {
      return
    }

    cancelRequest(notifyApplicant)
  }

  if (loading || stage === 'success') {
    return (
      <Stack alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={24} />
      </Stack>
    )
  }

  return (
    <>
      {stage === 'idle' ? (
        <Button
          variant="outlined"
          fullWidth
          onClick={() => setStage('confirm')}
          sx={{
            minHeight: 45,
            borderRadius: '8px',
            borderColor: '#9b006a',
            color: '#9b006a',
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: '0.14px',
            textTransform: 'none',
            px: 3,
            '&:hover': {
              borderColor: '#9b006a',
              backgroundColor: 'rgba(155, 0, 106, 0.06)',
            },
          }}
        >
          Cancel
        </Button>
      ) : (
        <Stack spacing={2}>
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#29222a',
                mb: 1,
              }}
            >
              Please Confirm
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                color: '#534e54',
                m: 0,
              }}
            >
              Are you sure you want to cancel this request?
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ fontSize: '14px' }}>
              {error.message}
            </Alert>
          )}

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyApplicant}
                  onChange={handleCheckboxChange}
                  disabled={loading}
                  sx={{ alignSelf: 'center' }}
                />
              }
              label={
                <Typography
                  sx={{ fontSize: '14px', lineHeight: 1.4, textAlign: 'center' }}
                >
                  {`Inform the applicant we have cancelled this ${requestTypeText} 
                  request`}
                </Typography>
              }
              sx={{ alignItems: 'center', margin: 0 }}
            />
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => setStage('idle')}
              disabled={loading}
              sx={{
                borderRadius: '10px',
                borderColor: '#1b4554',
                color: '#1b4554',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '0.46px',
                textTransform: 'none',
                p: 2,
                '&:hover': {
                  borderColor: '#1b4554',
                  backgroundColor: 'rgba(27, 69, 84, 0.06)',
                },
              }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              sx={{
                borderRadius: '10px',
                borderColor: '#6c0049',
                color: '#6c0049',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '0.46px',
                textTransform: 'none',
                p: 2,
                '&:hover': {
                  borderColor: '#6c0049',
                  backgroundColor: 'rgba(237, 28, 36, 0.06)',
                },
              }}
            >
              Confirm cancel
            </Button>
          </Stack>
        </Stack>
      )}
    </>
  )
}

export default CancelRequest
